import { useDispatch, useSelector } from "react-redux";
import { markPaid } from "../../../services/apiService";
import { FormEntry } from "../../../types/types";
import { formatDate } from "../../../utils/utils";
import { Link } from "react-router-dom";
import { AppDispatch, RootState } from "../../../store";
import { useNotification } from "../../../context/NotificationContext";
import { fetchFormEntries } from "../../../store/slices/formEntriesSlice";
import { formMappings } from "../../../config/formMappings";
import { formFields } from "../../../services/form_fields";

export default function RegistrantHeader(props: { entry: FormEntry }) {
  const { showNotification } = useNotification();
  const dispatch = useDispatch<AppDispatch>();
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );

  // Get form mapping from Redux state
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];

  // Get paid field key from formMappings
  const paidFieldKey = formMapping?.fieldMappings?.paid;

  // Check paid status - for v1, API service maps field keys to human-readable names
  // For v2, field keys remain as-is (if not in form_fields.ts)
  let paidStatus = "";
  if (paidFieldKey) {
    // Check if API service mapped it to human-readable name
    const humanReadableName = formFields[paidFieldKey]?.name;
    if (humanReadableName && props.entry.meta[humanReadableName]) {
      // v1: Use human-readable name (API service mapped it)
      paidStatus = props.entry.meta[humanReadableName] || "";
    } else {
      // v2: Use raw field key (not mapped by API service)
      paidStatus = props.entry.meta[paidFieldKey] || "";
    }
  }
  // Fallback to human-readable name if mapping didn't work
  if (!paidStatus) {
    paidStatus = props.entry.meta["Paid?"] || "";
  }

  const hasPaid = paidStatus === "Yes" ? true : false;

  const updateEntries = async () => {
    const formMapping =
      formMappings[activeFormName as keyof typeof formMappings];
    if (formMapping) {
      await dispatch(fetchFormEntries(formMapping.formId));
    }
  };

  const handlePaid = async () => {
    if (props.entry) {
      try {
        await markPaid(props.entry.id, !hasPaid, activeFormName);
        showNotification({
          title: `Successfully Marked as ${hasPaid ? "Unpaid" : "Paid"}`,
          message: `Successfully marked registrant(s) as ${
            hasPaid ? "Unpaid" : "Paid"
          }.`,
          type: "success",
        });
        await updateEntries();
      } catch (error) {
        showNotification({
          title: `Marking ${hasPaid ? "Unpaid" : "Paid"} Failed`,
          message: `Failed to mark registrant(s) as ${
            hasPaid ? "Unpaid" : "Paid"
          }.`,
          type: "error",
        });
        console.error(
          `Failed to mark registrant(s) as ${hasPaid ? "Unpaid" : "Paid"}:`,
          error
        );
      }
    }
  };

  return (
    <section aria-labelledby="general-information-title">
      <div className="mb-8 md:flex md:items-center md:justify-between md:space-x-5">
        <div className="flex items-center space-x-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Confirmation #: {props.entry?.item_key}
            </h1>
            <p className="text-sm font-medium text-gray-500">
              Registered on{" "}
              <time dateTime={props.entry?.created_at}>
                {" "}
                {formatDate(props.entry?.created_at)}
              </time>
            </p>
          </div>
        </div>
        <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
          <span
            onClick={handlePaid}
            className="cursor-pointer inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-primary shadow-sm hover:bg-gray-200 border-gray-100 border-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
          >
            {hasPaid ? "Mark Unpaid" : "Mark Paid"}
          </span>
          {hasPaid && (
            <Link
              to={`/check-in/${props.entry?.item_key}`}
              className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
            >
              Check-In
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
